import React, { useState, useEffect, useRef, useCallback } from 'react';
import { connect, useSelector } from 'react-redux';
import { CFCProps } from 'types';
import { Search } from 'carbon-components-react';
import { useSaga } from 'use-reducer-saga';
import reducer, { initialState } from '../../localStore/reducer';
import sagas from '../../localStore/sagas';
import { searchJobIdUserRefStart } from '../../localStore/actions';
import { selectCurrentTeamId } from 'redux/team/selectors';
import Utils from 'utilities/helper.util';
import ResultsBox from './resultsBox';

const SearchText: React.FC<Props> = ({
  setSearchText,
  defaultSearchText,
  setCurrentPage,
  setPageOffset,
  searchFilter,
  setSearchInputValue,
  searchInputValue
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [localStore, localDispatch] = useSaga(reducer, initialState, sagas);
  const { searchJobIdUserRefData, isSearchingJobIdUserRef } = localStore;
  const currentTeamId = useSelector(selectCurrentTeamId);

  const [jobIdList, setJobIdList] = useState<Array<any>>([]);
  const [userRefList, setUserRefList] = useState<Array<any>>([]);
  const [isShowResult, setIsShowResult] = useState<Boolean>(false);
  const [noResult, setNoResult] = useState<boolean>(false);
  const [resultClicked, setResultClicked] = useState<Boolean>(false);
  const [localSearch, setLocalSearch] = useState<Boolean>(false);

  const getSearchData = useCallback(
    Utils.debounce(async (args: Array<any>) => {
      const searchFilter = args[4];
      const searchInputValue = args[0];
      const teamId = args[1];
      const queryParams: any = {
        text: searchInputValue,
        project: teamId ? [teamId] : undefined,
        status: searchFilter.status,
        viewAll: true,
        limit: 5,
        fields: ['id', 'external_id', 'user_reference'],
        range: searchFilter.range
      };
      localDispatch(searchJobIdUserRefStart(queryParams));
    }, 600),
    []
  );

  const handleOnChangeSearchText = (val: any) => {
    setResultClicked(false);
    setJobIdList([]);
    setUserRefList([]);
    setNoResult(false);
    if (val !== undefined) {
      setSearchInputValue(val);
      setLocalSearch(true);
      if (val === '') {
        setSearchText('');
        setLocalSearch(false);
      }
    } else {
      setSearchInputValue('');
      setSearchText('');
      setLocalSearch(false);
    }
  };

  const onClickResult = (item: any, type: string) => {
    setCurrentPage(1);
    setPageOffset(0);
    setResultClicked(true);
    setSearchInputValue(
      type === 'jobId' ? item.external_id : item.user_reference
    );
    setSearchText(type === 'jobId' ? item.external_id : item.user_reference);
    setIsShowResult(false);
    setLocalSearch(false);
  };

  useEffect(() => {
    if (
      typeof searchInputValue === 'string' &&
      searchInputValue?.trim()?.length > 1
    ) {
      if (!resultClicked && localSearch) {
        const defaultStartDate = Utils.getNewDate()
          .subtract(30, 'day')
          .startOf('day')
          .utc()
          .format();
        const defaultEndDate = Utils.getNewDate().endOf('day').utc().format();
        getSearchData(
          searchInputValue.trim(),
          currentTeamId,
          defaultStartDate,
          defaultEndDate,
          searchFilter
        );
        setIsShowResult(true);
      }
    } else {
      if (searchInputValue?.length <= 0) {
        setIsShowResult(false);
        setCurrentPage(1);
        setPageOffset(0);
      }
    }
  }, [
    searchInputValue,
    currentTeamId,
    getSearchData,
    resultClicked,
    searchFilter,
    localSearch,
    setCurrentPage,
    setPageOffset
  ]);

  useEffect(() => {
    setSearchText('');
    setSearchInputValue('');
  }, [currentTeamId, setSearchText, setSearchInputValue]);

  useEffect(() => {
    if (searchJobIdUserRefData) {
      setJobIdList(searchJobIdUserRefData.jobId);
      setUserRefList(searchJobIdUserRefData.userRef);
      if (
        searchJobIdUserRefData.jobId?.length === 0 &&
        searchJobIdUserRefData.userRef?.length === 0
      ) {
        setNoResult(true);
      }
    } else {
      setNoResult(false);
    }
  }, [searchJobIdUserRefData, isSearchingJobIdUserRef]);

  useEffect(() => {
    if (defaultSearchText) {
      setResultClicked(true);
      setSearchInputValue(defaultSearchText);
    }
  }, [defaultSearchText, setSearchInputValue]);

  return (
    <div ref={containerRef} className={'home-search-jobid-user-ref-container'}>
      <div className="search-textbox-container">
        <Search
          id="jobIdSearch"
          labelText=""
          value={searchInputValue}
          onChange={(e) => handleOnChangeSearchText(e.target.value)}
          placeHolderText={'Job ID or user reference ...'}
        />
      </div>
      {isShowResult && !isSearchingJobIdUserRef && (
        <div className="search-item-container">
          <ResultsBox
            jobIdItems={jobIdList}
            noResult={noResult}
            userRefItems={userRefList}
            onItemClick={onClickResult}
          />
        </div>
      )}
    </div>
  );
};

SearchText.displayName = 'SearchText';

const connector = connect();

type OwnProps = {
  defaultSearchText?: string;
  setSearchText: Function;
  setCurrentPage: Function;
  setPageOffset: Function;
  searchFilter: any;
  setSearchInputValue: Function;
  searchInputValue: string;
};

type Props = CFCProps<typeof connector> & OwnProps;

export default React.memo(connector(SearchText));
